import React from "react";
import "./Card.css";
import aboutGif from "../Assets/about-fabric.gif";
import { Link } from "react-router-dom";

const Card = () => {
  return (
    <div
      className="relative overflow-hidden px-6 py-12 sm:px-10 md:px-16 mb-6"
      style={{ background: "linear-gradient(135deg, #3A2060 0%, #6939AC 50%, #8353C6 100%)" }}
    >
      {/* Decorative orbs */}
      <div
        className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle, #E0D1F5 0%, transparent 70%)" }}
      />
      <div
        className="absolute -bottom-16 -left-16 w-60 h-60 rounded-full opacity-15 pointer-events-none"
        style={{ background: "radial-gradient(circle, #E0D1F5 0%, transparent 70%)" }}
      />
      {/* Dot grid */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-10">

        {/* ── Left: GIF card ── */}
        <div className="w-full lg:w-[44%] flex-shrink-0">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-purple-100">
            {/* Card header */}
            <div className="px-5 py-3 border-b border-purple-100 flex items-center gap-2"
                 style={{ background: "linear-gradient(90deg, #EEE5FA, #EEE5FA)" }}>
              <div className="w-2 h-2 rounded-full bg-purple-400" />
              <div className="w-2 h-2 rounded-full bg-purple-300" />
              <div className="w-2 h-2 rounded-full bg-purple-200" />
              <span className="ml-2 text-xs font-semibold text-purple-700 uppercase tracking-widest">
                Custom Print Process
              </span>
            </div>
            {/* Steps */}
            <div className="flex flex-col items-center gap-3 p-5">
              <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-full px-4 py-1.5">
                <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center font-bold">1</span>
                <span className="text-sm font-semibold text-gray-800">We will <span className="text-purple-700">Develop Design</span></span>
              </div>

              <img
                crossOrigin="anonymous"
                loading="lazy"
                src={aboutGif}
                alt="Custom fabric process"
                className="w-full max-w-[280px] h-[240px] object-cover rounded-xl shadow-md border border-purple-100"
              />

              <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-full px-4 py-1.5">
                <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center font-bold">2</span>
                <span className="text-sm font-semibold text-gray-800">We will <span className="text-purple-700">print</span> on fabric of your choice</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: Content ── */}
        <div className="w-full lg:w-[56%] flex flex-col gap-5 text-white">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/15 border border-white/25 rounded-full px-4 py-1.5 w-fit">
            <span>🧵</span>
            <span className="text-xs font-semibold uppercase tracking-widest">Premium Custom Fabric</span>
          </div>

          {/* Heading */}
          <div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight"
              style={{ fontFamily: "'Times New Roman', Serif" }}
            >
              About Our Fabric
            </h2>
            <div className="flex items-center gap-3 mt-2">
              <div className="h-px w-10 bg-white/30" />
              <div className="w-1.5 h-1.5 rotate-45 bg-purple-300" />
              <div className="h-px w-10 bg-white/30" />
            </div>
          </div>

          {/* Description */}
          <p className="text-white/80 text-sm sm:text-base leading-relaxed">
            Struggling to find unique fabric designs? We've got you covered! Whether you're a fabric shop owner or a designer, we can help bring your ideas to life. Simply share your designs, select from our premium fabrics, and we'll print them — with a minimum order of just <span className="text-purple-200 font-semibold">1 meter</span>.
          </p>
          <p className="text-white/70 text-sm leading-relaxed">
            We use high-quality, eco-friendly printing methods to deliver vibrant and long-lasting results while ensuring your designs remain <span className="text-purple-200 font-semibold">confidential</span>. A dedicated expert will guide you through every step, from design to delivery.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-2">
            {[
              { icon: "📏", text: "Min. 1 Meter Order" },
              { icon: "🌿", text: "Eco-Friendly Printing" },
              { icon: "🔒", text: "Design Confidential" },
              { icon: "🚀", text: "Fast Delivery" },
            ].map(({ icon, text }) => (
              <span
                key={text}
                className="flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-xs font-medium px-4 py-1.5 rounded-full"
              >
                <span>{icon}</span>
                {text}
              </span>
            ))}
          </div>

          {/* CTA */}
          <div className="flex gap-3 mt-1">
            <Link to="/explore-our-ideas">
              <button className="inline-flex items-center gap-2 bg-white text-purple-700 font-bold py-3 px-8 text-sm rounded-full shadow-xl hover:bg-purple-50 hover:scale-105 active:scale-95 transition-all duration-300">
                Explore Our Ideas ✨
              </button>
            </Link>
            <a
              href="https://wa.me/919944592904?text=Hi%2C%20I%27m%20interested%20in%20custom%20fabric%20printing"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold py-3 px-6 text-sm rounded-full hover:bg-white/10 transition-all duration-300"
            >
              Get Quote
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Card;
